import React, { useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

interface DialogHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface DialogBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface DialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Dialog({ open, onClose, children, className }: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const trapFocus = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab" || !dialogRef.current) return;

    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = "hidden";

      const timer = setTimeout(() => {
        const focusable =
          dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        focusable?.[0]?.focus();
      }, 50);

      document.addEventListener("keydown", trapFocus);

      return () => {
        clearTimeout(timer);
        document.removeEventListener("keydown", trapFocus);
      };
    } else {
      document.body.style.overflow = "";
      previousFocusRef.current?.focus();
    }
  }, [open, trapFocus]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[50] flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            className="absolute inset-0 bg-black/40 backdrop-blur-[var(--blur-sm)]"
            onClick={onClose}
            aria-hidden
          />

          {/* Dialog surface */}
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{
              duration: 0.3,
              ease: [0.2, 0.8, 0.2, 1],
            }}
            className={cn(
              "relative z-10 w-full max-w-lg",
              "rounded-[var(--radius-2xl)]",
              "bg-[var(--bg-surface)] backdrop-blur-[var(--blur-lg)]",
              "border border-[var(--border-hairline)]",
              "shadow-[var(--shadow-lg)]",
              className,
            )}
          >
            <button
              onClick={onClose}
              aria-label="Close dialog"
              className={cn(
                "absolute right-3 top-3 z-10",
                "inline-flex h-7 w-7 items-center justify-center rounded-full",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                "hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-[var(--duration-fast)] ease-[var(--easing-default)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cool-azure)]/50",
              )}
            >
              <X size={16} />
            </button>
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

function DialogHeader({ children, className }: DialogHeaderProps) {
  return (
    <div
      className={cn(
        "px-6 pt-6 pb-0",
        "text-base font-semibold text-[var(--text-primary)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function DialogBody({ children, className }: DialogBodyProps) {
  return (
    <div
      className={cn(
        "px-6 py-4",
        "text-sm text-[var(--text-secondary)] leading-relaxed",
        "max-h-[70vh] overflow-y-auto",
        className,
      )}
    >
      {children}
    </div>
  );
}

function DialogFooter({ children, className }: DialogFooterProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2",
        "px-6 pb-6 pt-2",
        className,
      )}
    >
      {children}
    </div>
  );
}

Dialog.displayName = "Dialog";
DialogHeader.displayName = "DialogHeader";
DialogBody.displayName = "DialogBody";
DialogFooter.displayName = "DialogFooter";

export { Dialog, DialogHeader, DialogBody, DialogFooter };
export type { DialogProps, DialogHeaderProps, DialogBodyProps, DialogFooterProps };
